import { useLocalStorage } from "@mantine/hooks";
import {
	AGENT_CHAT_DRAWER_DEFAULT_WIDTH_PX,
	AGENT_CHAT_DRAWER_MAX_VIEWPORT_RATIO,
	AGENT_CHAT_DRAWER_MAX_WIDTH_PX,
	AGENT_CHAT_DRAWER_MIN_WIDTH_PX,
	AGENT_CHAT_DRAWER_WIDTH_STORAGE_KEY,
} from "@src/components/agent-chat/constants";
import { type MouseEvent as ReactMouseEvent, useCallback, useRef } from "react";

/** Clamp preferred width for storage / drag. Live viewport caps use CSS. */
export function clampAgentChatDrawerWidth(
	widthPx: number,
	viewportWidthPx: number = typeof window !== "undefined"
		? window.innerWidth
		: AGENT_CHAT_DRAWER_MAX_WIDTH_PX,
): number {
	const viewportCap = Math.floor(
		viewportWidthPx * AGENT_CHAT_DRAWER_MAX_VIEWPORT_RATIO,
	);
	const maxWidth = Math.min(AGENT_CHAT_DRAWER_MAX_WIDTH_PX, viewportCap);

	return Math.max(AGENT_CHAT_DRAWER_MIN_WIDTH_PX, Math.min(widthPx, maxWidth));
}

function parseWidthPx(value: string): number {
	const parsed = Number.parseFloat(value);

	if (!Number.isFinite(parsed)) {
		return AGENT_CHAT_DRAWER_DEFAULT_WIDTH_PX;
	}

	return parsed;
}

function formatWidthPx(widthPx: number): string {
	return `${widthPx}px`;
}

export function useAgentChatDrawerWidth() {
	const [storedWidth, setStoredWidth] = useLocalStorage({
		key: AGENT_CHAT_DRAWER_WIDTH_STORAGE_KEY,
		defaultValue: formatWidthPx(AGENT_CHAT_DRAWER_DEFAULT_WIDTH_PX),
	});
	const drawerRef = useRef<HTMLDivElement>(null);

	const widthPx = clampAgentChatDrawerWidth(parseWidthPx(storedWidth));
	const drawerWidth = formatWidthPx(widthPx);

	const handleResizeMouseDown = useCallback(
		(event: ReactMouseEvent) => {
			event.preventDefault();
			const startX = event.clientX;
			const startWidth =
				drawerRef.current?.getBoundingClientRect().width ?? widthPx;

			const handleMouseMove = (moveEvent: MouseEvent) => {
				// Left-edge drag: moving left grows the panel.
				const nextWidth = startWidth - (moveEvent.clientX - startX);
				const clamped = clampAgentChatDrawerWidth(nextWidth);
				setStoredWidth(formatWidthPx(clamped));
			};

			const handleMouseUp = () => {
				document.removeEventListener("mousemove", handleMouseMove);
				document.removeEventListener("mouseup", handleMouseUp);
				document.body.style.cursor = "";
			};

			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);
			document.body.style.cursor = "col-resize";
		},
		[setStoredWidth, widthPx],
	);

	return {
		drawerRef,
		drawerWidth,
		widthPx,
		handleResizeMouseDown,
	};
}
